import type { PaymentHistoryItem } from '@/types/application'
import { formatAmount } from '@/lib/utils/formatters'

interface PaymentLogTableProps {
    paymentHistory: PaymentHistoryItem[]
    applicantId?: string
}

/** Angular parity: hide APPLICATION_FEE rows belonging to other applicants. */
export function filterPaymentLogForApplicant(
    paymentHistory: PaymentHistoryItem[] | null | undefined,
    applicantId?: string,
): PaymentHistoryItem[] {
    if (!paymentHistory?.length) {
        return []
    }

    return paymentHistory.filter((payment) => {
        if (['APPLICATION_FEE', 'APPLICATION_FEE_REFUND'].includes(payment.payment_type || '')) {
            return String(payment.fk_applicant_id) === String(applicantId)
        }
        return true
    })
}

export function PaymentLogTable({ paymentHistory, applicantId }: PaymentLogTableProps) {
    const visiblePayments = filterPaymentLogForApplicant(paymentHistory, applicantId)

    if (visiblePayments.length === 0) {
        return null
    }

    return (
        <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-3">
            <b>Payment Log</b>
            <div className="overflow-x-auto touch-scroll mt-3">
                <table className="w-full border-collapse border border-gray-300 bg-white">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Method</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Base Fee</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Service Fee</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Total</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Log ID</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Transaction</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Date / Time</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#94845b] border border-gray-300">Paid By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visiblePayments
                            .map((payment, index) => {
                                // Angular-matching calculations
                                const paymentAmount = payment.payment_amount ? Number(payment.payment_amount) : 0
                                const surcharge = payment.payment_log_surcharge_fee ? Number(payment.payment_log_surcharge_fee) : 0
                                const refundFee = payment.payment_log_refund_fee ? Number(payment.payment_log_refund_fee) : 0
                                // Base Fee: subtract surcharge if it exists and is less than payment_amount; otherwise handle refund_fee
                                const baseFee = (surcharge && surcharge < paymentAmount)
                                    ? (paymentAmount - surcharge)
                                    : (refundFee ? (paymentAmount + refundFee * -1) : paymentAmount)
                                // Service Fee: surcharge or refund_fee or $0
                                const serviceFee = surcharge || refundFee || 0
                                // Total: just payment_amount (Angular uses payment_amount directly)
                                const total = paymentAmount
                                const paymentDate = payment.created_at
                                    ? new Date(Number(payment.created_at) * 1000).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: true
                                    }).replace(',', '')
                                    : 'N/A'
                                // Status: Angular keeps ACH_Check_Pending as-is, titlecases others
                                const displayStatus = payment.payment_status === 'ACH_Check_Pending'
                                    ? 'ACH_Check_Pending'
                                    : (payment.payment_status
                                        ? payment.payment_status.charAt(0).toUpperCase() + payment.payment_status.slice(1).toLowerCase()
                                        : '')
                                // Transaction: Angular uses auth_code for DEFAULT type
                                const transactionDisplay = payment.payment_type === 'DEFAULT'
                                    ? (payment.auth_code || 'N/A')
                                    : (payment.transaction_id || 'N/A')

                                return (
                                    <tr key={payment.payment_log_id || index} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{payment.payment_type || 'N/A'}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{payment.payment_source || 'N/A'}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{formatAmount(baseFee)}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{formatAmount(serviceFee)}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{formatAmount(total)}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{payment.payment_log_id || 'N/A'}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{transactionDisplay}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{paymentDate}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{displayStatus}</td>
                                        <td className="px-3 py-2 text-sm text-[#333] border border-gray-300">{payment.paid_by || 'N/A'}</td>
                                    </tr>
                                )
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
